import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking and performance monitoring.
 * Only initializes if DSN is provided (production/staging).
 * Safe to call multiple times - Sentry handles re-initialization gracefully.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || "development";
  
  // Only initialize if DSN is provided (production/staging)
  if (!dsn) {
    // Silently skip initialization in development if DSN not set
    if (environment === "development") {
      console.log("[Sentry] DSN not configured, skipping initialization");
    }
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      
      // Disable verbose debug logging - only enable if explicitly needed for troubleshooting
      debug: false,
      
      // Performance monitoring
      tracesSampleRate: environment === "production" ? 0.1 : 1.0, // 10% in prod, 100% in staging
      
      // Session replay (optional, can be expensive)
      replaysSessionSampleRate: 0.0, // Disabled by default
      replaysOnErrorSampleRate: 0.1, // 10% of errors get session replay
      
      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true, // Privacy: mask all text in replays
          blockAllMedia: true, // Privacy: block all media in replays
        }),
      ],
      
      // Filter out noisy errors
      ignoreErrors: [
        // Browser extensions
        "top.GLOBALS",
        "originalCreateNotification",
        "canvas.contentDocument",
        "MyApp_RemoveAllHighlights",
        "atomicFindClose",
        "fb_xd_fragment",
        "bmi_SafeAddOnload",
        "EBCallBackMessageReceived",
        // Network errors that are often not actionable
        "NetworkError",
        "Network request failed",
        // Third-party scripts
        "Non-Error promise rejection captured",
      ],
      
      // Filter out specific URLs (e.g., browser extensions)
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
      ],
      
      // Only send errors in production/staging
      beforeSend(event, hint) {
        // Don't send in development unless explicitly testing
        if (environment === "development" && !import.meta.env.VITE_SENTRY_ENABLE_DEV) {
          return null;
        }
        
        // Filter out known non-critical errors
        const error = hint.originalException;
        if (error && typeof error === "object" && "message" in error) {
          const message = String(error.message);
          // Ignore specific Supabase errors that are handled gracefully
          if (message.includes("schema cache") || message.includes("PGRST204")) {
            return null;
          }
          // Ignore blocked by client errors (ad blockers, privacy extensions)
          if (message.includes("ERR_BLOCKED_BY_CLIENT") || message.includes("blocked by client")) {
            return null;
          }
        }
        
        return event;
      },
      
      
      // Release tracking (optional - set via build process)
      release: import.meta.env.VITE_SENTRY_RELEASE || `buukd@${import.meta.env.MODE}-${new Date().toISOString().split('T')[0]}`,
    });
    
    // Note: If you see "ERR_BLOCKED_BY_CLIENT" errors in the console, 
    // this is likely due to an ad blocker or privacy extension blocking Sentry requests.
    // This is harmless and won't affect production (where ad blockers are less common).
    // Sentry will still work normally when requests aren't blocked.
  } catch (error) {
    // If Sentry initialization fails, don't break the app
    console.error("[Sentry] Failed to initialize:", error);
  }
}

/**
 * Set user context for Sentry (call after user logs in)
 */
export function setSentryUser(user: { id: string; email?: string; name?: string } | null) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  
  try {
    Sentry.setUser(user ? {
      id: user.id,
      email: user.email,
      username: user.name,
    } : null);
  } catch (error) {
    console.error("[Sentry] Failed to set user:", error);
  }
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    // Fallback to console if Sentry not configured
    console.error("[Error]", error, context);
    return;
  }
  
  try {
    // captureException returns a string (event ID), not a Promise
    // Transport errors are handled internally by Sentry
    const eventId = Sentry.captureException(error, {
      contexts: {
        custom: context || {},
      },
      tags: context?.test ? { test: "true", verification: "true" } : undefined,
    });
    
    // Log the event ID for debugging
    if (import.meta.env.DEV || import.meta.env.VITE_SENTRY_DEBUG === "true") {
      console.log(`[Sentry] Captured exception with event ID: ${eventId}`);
    }
  } catch (err) {
    // If Sentry fails, at least log to console
    console.error("[Error]", error, context);
  }
}

/**
 * Capture a message manually
 */
export function captureMessage(message: string, level: "info" | "warning" | "error" = "info", context?: Record<string, any>) {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.log(`[${level.toUpperCase()}]`, message, context);
    return;
  }
  
  try {
    Sentry.captureMessage(message, {
      level,
      contexts: {
        custom: context || {},
      },
    });
  } catch (err) {
    console.log(`[${level.toUpperCase()}]`, message, context);
  }
}
